import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, matchRoutes } from "react-router-dom";
import cookie from "react-cookies";

import { LogoutHook, useAPI } from "services/Api";
import ModularHeader, { ModularHeaderConfig } from "pages/ModularHeader";
import SideMenu from "pages/SideMenu";
import StickyFooter from "pages/StickyFooter";
import { CustomRouteObject } from "types/CustomRouteObject";
import { routeConfig } from "../App";
import { unhashAccessPath } from "services/AccessPathUtils";
import { useNotificationManager } from "notifications/NotificationManager";
import { useSessionTimeout } from "utility/SessionManager";

const SESSION_EXPIRATION_TIMEOUT = import.meta.env.VITE_BUILD_ENVIRONMENT === "Production" ? 900 : 900; // Session expiration value in seconds (900 seconds = 15 minutes).

interface PageProps {
    children: React.ReactNode;
    componentName: string;
    headerConfig?: ModularHeaderConfig;
    internal?: boolean;
    // Callbacks
    logoutHook?: LogoutHook;
}

const Page: React.FC<PageProps> = ({
    children,
    componentName,
    headerConfig = null,
    internal = true,
    // Callbacks
    logoutHook = null
}: PageProps) => {

    const API = useAPI();

    useEffect(() => {
        console.log(`🚨 MOUNTED ${componentName}`);
        return () => {
            console.log(`💥 UNMOUNTED ${componentName}`);
        };
    }, [componentName]);

    const [stickyFooter, setStickyFooter] = useState(null);

    const role = cookie.load("role") ? cookie.load("role") : "None";
    const user = cookie.load("user") ? cookie.load("user") : null;
    const token = cookie.load("token") ? cookie.load("token") : null;

    const location = useLocation();
    const navigate = useNavigate();
    const notificationManager = useNotificationManager();

    // ---=== Utilities ===--- //

    const componentRequiresLogin = () => {
        const matches = matchRoutes(routeConfig, location);

        if (matches && matches.length > 0) {
            const currentRoute = matches[matches.length - 1].route as CustomRouteObject;
            return currentRoute.requiresAuth ?? true;
        }

        return true;
    };

    const getStickyFooter = () => {
        if (stickyFooter) {
            return stickyFooter;
        }

        let footer = <StickyFooter internal={internal} parentComponentName={componentName}/>;
        setStickyFooter(footer);
        return footer;
    };

    const handleLogout = (friendlyMessage: string, logoutReason: string, routeToLogin = true) => {
        if (!logoutHook && friendlyMessage) {
            notificationManager.addSuccess(friendlyMessage);
        }
        void API.api_Logout(componentName, logoutReason, logoutHook, null, routeToLogin);
    };

    // ---=== Session Timeout Timer ===--- //

    useSessionTimeout({
        timeout: SESSION_EXPIRATION_TIMEOUT,
        onTimeout: (userMessage, logMessage) => {
            handleLogout(userMessage, logMessage);
        },
        parentComponentName: componentName,
        enabled: componentRequiresLogin()
    });

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            if (unhashAccessPath(componentName) === "[ValidateEmail]") {
                handleLogout(null, "Account Confirm Email Page Displayed", false);
                return;
            }

            if (user === null) {
                if (!cookie.load("user") || !cookie.load("token")) {
                    if (componentRequiresLogin()) {
                        handleLogout("Your session timed out. Please login again.", `Missing Cookies: user[${user}] userCookie[${cookie.load("user")}] token[${token}] tokenCookie[${cookie.load("token")}] role[${role}] roleCookie[${cookie.load("role")}]`);
                        return;
                    }
                }
            }

            if (role && role !== "None") {
                switch (unhashAccessPath(componentName)) {
                    case "[ExternalReports]":
                    case "[ReportReview]":
                        break;
                    default:
                        if (role.toLowerCase().includes("admin") && !location.pathname.includes("reportsExternal")) {
                            void navigate("/reportsExternal");
                        }
                        break;
                }
            }
        }, [user, role]
    );

    // ---=== Render ===--- //

    return (
        <React.Fragment>
            <div className={`flex h-screen bg-white select-none`}>
                {
                    internal && (
                        <SideMenu
                            parentComponentName={componentName}
                            role={role ? role : "None"}
                            userName={user ? `${user.first_name} ${user.last_name}` : "user"}
                            // Callbacks
                            logoutHook={logoutHook}
                        />
                    )
                }
                <div
                    id="page-container"
                    className={`flex flex-col w-full h-full bg-white`}
                >
                    <div id="header-container" className="z-100 relative w-full">
                        <header id="header" className={"z-101 flex justify-center w-full shadow-md bg-white pointer-events-auto"}>
                            <ModularHeader
                                headerConfig={headerConfig}
                                internal={internal}
                            >
                            </ModularHeader>
                        </header>
                    </div>
                    <div id="child-container" className={"z-25 relative flex-1 overflow-auto bg-white"}>
                        {children}
                    </div>
                    {getStickyFooter()}
                </div>
            </div>
        </React.Fragment>
    );
};

export default Page;
